import { ComponentType, ReactNode, useEffect, useRef, useState } from "react"
import Select, {
  components,
  FilterOptionOption,
  GroupBase,
  MenuListProps,
  MenuProps,
  MultiValue,
  NoticeProps,
  OptionProps,
  SingleValue,
  SingleValueProps,
} from "react-select"
import classNames from "classnames"

import "./select.scss"

import { UITypes } from "types"

import { isIOSDevice, isMobileDevice } from "utils/deviceDetection"

import styles from "./index.module.scss"

export interface SelectStaticProps {
  dataTestId?: string
  label?: string
  placeholder?: string
  values: UITypes.Option[]
  name?: string
  disabled?: boolean
  errorMessage?: string
  options?: UITypes.Option[]
  multi?: boolean
  isLoading?: boolean
  className?: string
  minWidth?: string
  backspaceDelete?: boolean
  skipOpeningDropdown?: boolean
  menuClassName?: string
  isSearchable?: boolean
  showSelectAllOption?: boolean
  onChange?: (value: UITypes.Option[]) => void
  contentRenderer?: () => ReactNode
  optionRenderer?: ComponentType<OptionProps<UITypes.Option, boolean, GroupBase<UITypes.Option>>>
  singleValueRenderer?: ComponentType<SingleValueProps<UITypes.Option, boolean, GroupBase<UITypes.Option>>>
  noOptionsMessage?: ComponentType<NoticeProps<UITypes.Option, boolean, GroupBase<UITypes.Option>>>
  onSearchChange?: (option: FilterOptionOption<UITypes.Option>, inputValue: string) => boolean
  onMenuClose?: () => void
  onInputChange?: (inputValue: string) => void
}

export const SelectStatic = ({
  dataTestId,
  label,
  options = [],
  placeholder,
  singleValueRenderer,
  disabled,
  values = [],
  name,
  multi,
  isLoading,
  errorMessage,
  className,
  onChange,
  minWidth,
  backspaceDelete = false,
  skipOpeningDropdown = false,
  contentRenderer,
  menuClassName,
  optionRenderer,
  noOptionsMessage,
  isSearchable = true,
  showSelectAllOption = false,
  onSearchChange,
  onMenuClose,
  onInputChange,
}: SelectStaticProps) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const selectRef = useRef<HTMLDivElement>(null)
  const menuListScrollRef = useRef<number>(0)
  const menuListElementRef = useRef<HTMLDivElement | null>(null)
  const mobileDevice = isMobileDevice()
  const isIOS = isIOSDevice()

  const handleCloseMenu = () => {
    setMenuIsOpen(false)
  }

  useEffect(() => {
    if (isIOS) return
    if (!mobileDevice) {
      window.removeEventListener("orientationchange", handleCloseMenu)
    } else {
      window.removeEventListener("resize", handleCloseMenu)
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setIsVisible(entry.isIntersecting)
          if (!entry.isIntersecting && menuIsOpen) {
            handleCloseMenu()
          }
        })
      },
      {
        threshold: [0.01],
        rootMargin: "-20px",
      },
    )

    if (selectRef.current) {
      observer.observe(selectRef.current)
    }

    return () => {
      if (mobileDevice) {
        window.removeEventListener("orientationchange", handleCloseMenu)
      } else {
        window.removeEventListener("resize", handleCloseMenu)
      }
      if (selectRef.current) {
        observer.unobserve(selectRef.current)
      }
      observer.disconnect()
    }
  }, [menuIsOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setMenuIsOpen(false)
      }
    }

    if (menuIsOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [menuIsOpen, contentRenderer])

  const handleClick = () => {
    if (selectRef.current && !isVisible) {
      const parentElement = selectRef.current.parentElement

      if (parentElement && parentElement.scrollTo) {
        const elementPosition = selectRef.current.offsetTop
        parentElement.scrollTo({
          top: elementPosition,
        })
      }
    }
  }

  const handleChange = (selected: MultiValue<UITypes.Option> | SingleValue<UITypes.Option>) => {
    handleClick()
    if (multi && menuListElementRef.current) {
      menuListScrollRef.current = menuListElementRef.current.scrollTop
    }

    if (onChange) {
      if (multi) {
        onChange(selected ? [...(selected as MultiValue<UITypes.Option>)] : [])
      } else {
        onChange(selected ? [selected as UITypes.Option] : [])
      }
    }
  }

  const handleMenuClose = () => {
    setMenuIsOpen(false)
    if (onMenuClose) {
      onMenuClose()
    }
  }

  const handleSearch = (option: FilterOptionOption<UITypes.Option>, inputValue: string) => {
    if (onSearchChange) {
      return onSearchChange(option, inputValue)
    }
    return true
  }

  const Option = (props: OptionProps<UITypes.Option, boolean, GroupBase<UITypes.Option>>) => {
    const isMouseEventRef = useRef(false)

    const setRef = (node: HTMLDivElement | null) => {
      if (node) {
        const originalScrollIntoView = node.scrollIntoView.bind(node)
        node.scrollIntoView = function (arg?: boolean | ScrollIntoViewOptions) {
          if (!isMouseEventRef.current) {
            originalScrollIntoView(arg)
          }
        }

        const handleMouseEnter = () => {
          isMouseEventRef.current = true
        }
        const handleMouseLeave = () => {
          isMouseEventRef.current = false
        }
        const handleKeyDown = () => {
          isMouseEventRef.current = false
        }

        node.addEventListener("mouseenter", handleMouseEnter)
        node.addEventListener("mouseleave", handleMouseLeave)
        node.addEventListener("keydown", handleKeyDown)
      }

      if (props.innerProps?.ref) {
        if (typeof props.innerProps.ref === "function") {
          props.innerProps.ref(node)
        } else {
          ;(props.innerProps.ref as React.MutableRefObject<HTMLDivElement | null>).current = node
        }
      }
    }

    const innerProps = props.innerProps
      ? {
          ...props.innerProps,
          ref: setRef,
        }
      : { ref: setRef, onMouseMove: undefined }

    return <components.Option {...props} innerProps={innerProps} />
  }

  const Menu = (props: MenuProps<UITypes.Option, boolean, GroupBase<UITypes.Option>>) => {
    const menuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (isIOS && menuIsOpen && menuRef.current) {
        const timer = setTimeout(() => {
          if (menuRef.current) {
            menuRef.current.focus()
            menuRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" })
          }
        }, 150)

        return () => clearTimeout(timer)
      }
    }, [menuIsOpen, isIOS])

    return (
      <components.Menu
        {...props}
        {...(isIOS && { innerRef: menuRef })}
        className={classNames(props.className, menuClassName || "", { "ios-focus": isIOS })}
      />
    )
  }

  const handleSelectAll = () => {
    if (onChange && multi && options.length > 0) {
      onChange([...options])
      handleMenuClose()
    }
  }

  const handleUnselectAll = () => {
    if (onChange && multi) {
      onChange([])
      handleMenuClose()
    }
  }

  const MenuList = (props: MenuListProps<UITypes.Option, boolean, GroupBase<UITypes.Option>>) => {
    const showSelectAll = showSelectAllOption && multi && options.length > 0
    const allSelected = values.length === options.length && options.length > 0

    const setMenuListRef = (node: HTMLDivElement | null) => {
      menuListElementRef.current = node
    }

    useEffect(() => {
      if (menuListElementRef.current && multi && menuListScrollRef.current > 0) {
        menuListElementRef.current.scrollTop = menuListScrollRef.current
      }
    }, [values, multi])

    return (
      <components.MenuList
        {...props}
        innerRef={setMenuListRef}
        className={classNames(props.className, { "mobile-menu": mobileDevice })}
      >
        {showSelectAll && (
          <div
            onMouseDown={() => {
              if (allSelected) {
                handleUnselectAll()
              } else {
                handleSelectAll()
              }
            }}
            tabIndex={-1}
            className={classNames("static-select__option", "static-select__select-all-option", {
              "static-select__option--is-selected": allSelected,
            })}
          >
            {allSelected ? "Unselect All" : "Select All"}
          </div>
        )}
        {props.children}
      </components.MenuList>
    )
  }

  return (
    <div className={classNames(styles.fieldWrapper, className)} ref={selectRef} data-testid={dataTestId}>
      {label && <div className={styles.fieldLabelNew}>{label}</div>}
      <div className={classNames(styles.field)}>
        <Select
          isDisabled={disabled}
          options={options}
          value={values}
          isSearchable={isSearchable}
          isLoading={isLoading}
          classNamePrefix="static-select"
          className={classNames("custom", {
            [styles.error]: errorMessage,
            [styles.disabled]: disabled,
          })}
          classNames={{
            control: () => (errorMessage ? "error" : ""),
          }}
          name={name}
          styles={{
            control: base => ({
              ...base,
              minWidth: minWidth,
            }),
            menuPortal: base => ({
              ...base,
              zIndex: 10000,
            }),
          }}
          isMulti={multi}
          placeholder={placeholder}
          onChange={handleChange}
          filterOption={onSearchChange ? handleSearch : undefined}
          onInputChange={onInputChange}
          components={{
            ...(contentRenderer
              ? {
                  ValueContainer: () => <div onClick={() => setMenuIsOpen(prev => !prev)}>{contentRenderer()}</div>,
                }
              : undefined),
            ...(optionRenderer
              ? {
                  Option: optionRenderer,
                }
              : {
                  Option: Option,
                }),
            ...(singleValueRenderer
              ? {
                  SingleValue: singleValueRenderer,
                }
              : undefined),
            ...(noOptionsMessage
              ? {
                  NoOptionsMessage: noOptionsMessage,
                }
              : undefined),
            Menu: Menu,
            MenuList: MenuList,
          }}
          isClearable={backspaceDelete}
          menuPosition={isIOS ? "absolute" : "fixed"}
          menuPortalTarget={isIOS ? undefined : document.body}
          menuIsOpen={skipOpeningDropdown ? false : menuIsOpen}
          onMenuOpen={() => {
            setMenuIsOpen(true)
            handleClick()
          }}
          onMenuClose={handleMenuClose}
          hideSelectedOptions={false}
          closeMenuOnSelect={!multi}
        />
      </div>
      {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
    </div>
  )
}
